import { supabase } from '@/lib/supabaseClient';
import { TABLE_NAMES } from '@/lib/constants';
import type { ConceptoDeDeuda } from './debtConceptService';
import type { RegistroPago } from './paymentService';

export interface DeudaMensual {
  id_deuda: string;
  id_inquilino: string;
  id_edificio: string;
  mes_correspondiente: string; // Date string, e.g., YYYY-MM-DD
  monto_total_adeudado: number;
  monto_mantenimiento_calculado?: number;
  fecha_vencimiento: string; // Date string, e.g., YYYY-MM-DD
  esta_pagada?: boolean;
  saldo_pendiente: number;
  conceptos?: ConceptoDeDeuda[]; // Added for UI display
  payments?: RegistroPago[]; // Added for UI display in TenantStatementPage
}

export const debtService = {
  async getDebtsByInquilino(inquilinoId: string) {
    const { data, error } = await supabase
      .from(TABLE_NAMES.DEUDA_MENSUAL)
      .select(`
        *,
        conceptos:conceptodedeuda(*)
      `)
      .eq('id_inquilino', inquilinoId);
    if (error) throw error;
    return data;
  },

  async getAllDebts() {
    const { data, error } = await supabase
      .from(TABLE_NAMES.DEUDA_MENSUAL)
      .select(`
        *,
        conceptos:conceptodedeuda(*)
      `);
    if (error) throw error;
    return data;
  },

  async createDebt(debt: DeudaMensual, conceptos: ConceptoDeDeuda[]) {
    const { data: newDebt, error: debtError } = await supabase
      .from(TABLE_NAMES.DEUDA_MENSUAL)
      .insert([debt])
      .select();

    if (debtError) throw debtError;

    if (newDebt && newDebt.length > 0) {
      const deudaId = newDebt[0].id_deuda;
      const conceptosToInsert = conceptos.map(c => ({ ...c, id_deuda: deudaId }));
      const { data: newConcepts, error: conceptsError } = await supabase
        .from(TABLE_NAMES.CONCEPTO_DE_DEUDA)
        .insert(conceptosToInsert)
        .select();

      if (conceptsError) throw conceptsError;
      return { ...newDebt[0], conceptos: newConcepts };
    }
    return null;
  },

  async updateDebt(id: string, updates: Partial<DeudaMensual>) {
    const { data, error } = await supabase
      .from(TABLE_NAMES.DEUDA_MENSUAL)
      .update(updates)
      .eq('id_deuda', id)
      .select();
    if (error) throw error;
    return data[0];
  },

  async deleteDebt(id: string) {
    const { error } = await supabase
      .from(TABLE_NAMES.DEUDA_MENSUAL)
      .delete()
      .eq('id_deuda', id);
    if (error) throw error;
    return true;
  },
};
